import { router, useForm } from "@inertiajs/react";
import {
  Button,
  Card,
  Flex,
  Separator,
  Text,
  TextField,
} from "@radix-ui/themes";
import { LocateFixed, MapPin } from "lucide-react";
import { useState, type SubmitEvent } from "react";
import OnboardingShell from "@/shared/onboarding/OnboardingShell";
import { onboardingLayout } from "@/layouts/OnboardingLayout";
import type { OnboardingAreaProps } from "@/types/onboarding";

export default function OnboardingArea({
  home_area,
  ...shell
}: OnboardingAreaProps) {
  const { data, setData, patch, processing } = useForm({
    query: home_area ?? "",
  });
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);

  const handleSubmit = (e: SubmitEvent) => {
    e.preventDefault();
    patch("/welcome/area");
  };

  // Same browser prompt the tracks map uses. The server rounds what it gets.
  const locateMe = () => {
    if (!navigator.geolocation) {
      setLocateError(
        "Your browser can't share a location. Type a city or ZIP instead.",
      );
      return;
    }
    setLocating(true);
    setLocateError(null);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        router.patch(
          "/welcome/area",
          { latitude: coords.latitude, longitude: coords.longitude },
          { onFinish: () => setLocating(false) },
        ),
      () => {
        setLocating(false);
        setLocateError(
          "We couldn't get your location. Type a city or ZIP instead.",
        );
      },
    );
  };

  return (
    <OnboardingShell
      {...shell}
      errors={locateError ? [locateError] : shell.errors}
      title="Where are you, roughly?"
      subtitle="We'll show the tracks and events closest to you first. A city or ZIP is enough."
    >
      <Card size="3">
        <form onSubmit={handleSubmit}>
          <Flex direction="column" gap="4">
            <Text as="label" size="3">
              <Flex direction="column" gap="2">
                <Text weight="medium">City or ZIP</Text>
                <TextField.Root
                  size="3"
                  value={data.query}
                  onChange={(e) => setData("query", e.target.value)}
                  placeholder="Spokane, WA"
                >
                  <TextField.Slot>
                    <MapPin size={16} />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
            <Button
              size="3"
              type="submit"
              disabled={processing || locating || data.query.trim() === ""}
            >
              Continue
            </Button>
            <Flex align="center" gap="3">
              <Separator size="4" />
              <Text size="2" color="gray">
                or
              </Text>
              <Separator size="4" />
            </Flex>
            <Button
              size="3"
              type="button"
              variant="soft"
              onClick={locateMe}
              disabled={processing || locating}
            >
              <LocateFixed size={16} />
              {locating ? "Finding you…" : "Use my location"}
            </Button>
            <Text size="2" color="gray">
              We only keep a rough area, never your exact location.
            </Text>
          </Flex>
        </form>
      </Card>
    </OnboardingShell>
  );
}

OnboardingArea.layout = onboardingLayout;
